import { usePage } from "@inertiajs/react";
import { AlertOctagon, AlertTriangle, Bug, ExternalLink, Info, Sparkles, Wrench } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import TutorialBadge from "@/components/tutorial-badge";
import type { Announcement, SharedData } from "@/types";

const DEFAULT_BANNER_STYLE = "bg-gradient-to-b from-blue-600/95 via-blue-600/92 to-blue-700/90 text-white border-blue-700/40";

interface BannerConfig {
  icon: LucideIcon;
  style: string;
}

const BANNER_CONFIG: Record<Announcement["type"], BannerConfig> = {
  feature: {
    icon: Sparkles,
    style: DEFAULT_BANNER_STYLE,
  },
  bugfix: {
    icon: Bug,
    style: DEFAULT_BANNER_STYLE,
  },
  warning: {
    icon: AlertTriangle,
    style: DEFAULT_BANNER_STYLE,
  },
  info: {
    icon: Info,
    style: DEFAULT_BANNER_STYLE,
  },
  maintenance: {
    icon: Wrench,
    style: DEFAULT_BANNER_STYLE,
  },
  critical: {
    icon: AlertOctagon,
    style: "bg-gradient-to-b from-red-600 via-red-600 to-red-700 text-white border-red-700/40",
  },
};

export default function AppBetaBanner() {
  const { ui } = usePage<SharedData>().props;
  const bannerAnnouncement = ui?.banner_announcement;

  // Si hay un anuncio de banner, mostrar ese contenido
  if (bannerAnnouncement) {
    const config = BANNER_CONFIG[bannerAnnouncement.type] ?? BANNER_CONFIG.info;
    const Icon = config.icon;

    return (
      <div className={`flex w-full items-center justify-center px-4 sm:px-6 py-2.5 border-b ${config.style}`}>
        <div className="flex w-full max-w-5xl items-center justify-between gap-3">
          <div className="w-full sm:w-auto text-sm leading-6 flex items-center gap-2 sm:gap-3 flex-wrap">
            <span className="inline-flex items-center justify-center rounded-full bg-white/10 border border-white/20 text-white h-6 w-6">
              <Icon className="h-3.5 w-3.5" aria-hidden />
            </span>
            <strong className="font-medium text-white">{bannerAnnouncement.title}</strong>
            <span className="text-white/70">•</span>
            {bannerAnnouncement.action ? (
              <a
                href={bannerAnnouncement.action.url}
                target={bannerAnnouncement.action.target ?? "_self"}
                rel={bannerAnnouncement.action.target === "_blank" ? "noopener noreferrer" : undefined}
                className="text-white hover:text-white/90 hover:underline underline-offset-2 inline-flex items-center gap-1"
                aria-label={`${bannerAnnouncement.title}: ${bannerAnnouncement.content}`}
              >
                <span className="whitespace-normal">{bannerAnnouncement.content}</span>
                {bannerAnnouncement.action.target === "_blank" && (
                  <ExternalLink className="h-3.5 w-3.5 text-white/80" />
                )}
              </a>
            ) : (
              <span className="text-white whitespace-normal">{bannerAnnouncement.content}</span>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:block">
              <TutorialBadge variant="secondary" color="indigo" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Mostrar el banner por defecto si no hay anuncio
  return (
    <div className="flex w-full items-center justify-center bg-yellow-100 text-yellow-900 px-6 py-2.5 sm:px-3.5 border-b border-yellow-200">
      <div className="flex w-full max-w-5xl items-center justify-between gap-3">
        <a
          href="https://tally.so/r/nre9Ll"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Etapa de pruebas. Sugerencias o problemas: haz click aquí"
          className="text-sm/6 flex items-center justify-center gap-2 w-full sm:w-auto hover:text-yellow-950 hover:underline underline-offset-2"
        >
          <strong className="font-semibold">Etapa de pruebas</strong>
          <svg viewBox="0 0 2 2" aria-hidden="true" className="mx-1 inline size-0.5 fill-current">
            <circle r={1} cx={1} cy={1} />
          </svg>
          <span>Sugerencias o problemas: haz click aquí</span>
        </a>
        <div className="hidden sm:block shrink-0">
          <TutorialBadge variant="secondary" color="indigo" />
        </div>
      </div>
    </div>
  );
}
